import type { CashPaymentStartResponseDTO } from './checkoutApi.js';

const KEY_PREFIX = 'customer:checkout-cash-collect:';

/**
 * G4 — cash-collect sessionStorage call map (`usePhoneFirstCheckoutScreen`):
 *
 * | Op | Site | Notes |
 * |----|------|-------|
 * | write | `handleCash` success | After `startCash` |
 * | write | cold-hydrate effect | When poll `awaitingCashConfirmation===true` |
 * | read | sync init / session-bind remount | MOB-05 remount — **do not clear** on Back from cash waiting |
 * | read | `readCheckoutCashCollectResult` | Drop + clear key when `parsed.checkoutSessionId !== sessionId` (rebind) |
 * | clear | `continueAfterPay` | Guest leaves paid cash shell |
 * | clear | `recoverFromTerminalCheckoutSession` | Terminal / refuse → shop |
 * | clear | `handleExitPaymentFlow` | `paymentUiCompleted \|\| sessionTerminal` **or** soft OPEN (non-awaiting) |
 * | clear | `mountLiveStartQrOrDeny` (success) | Method switch → QR (incl. unpaid reuse) |
 * | clear | `handleGatewayStart` (success) | Method switch → gateway (`handleOtherPayment` / method select) |
 * | React | session-id rebind (`paymentBoundSessionId`) | `setCashCompleteResult(null)` — do not apply foreign session cash |
 *
 * React mirror: pair clears with `setCashCompleteResult(null)`; writes with `setCashCompleteResult(...)`.
 * Soft exit while `AWAITING_PAYMENT` keeps storage so cash waiting can remount (MOB-05).
 * Soft OPEN exit clears both React state and storage (no cash waiting to remount).
 */

function storageKey(sessionId: string): string {
  return `${KEY_PREFIX}${sessionId}`;
}

/** Survives checkout remount after SPA history POP (MOB-05) — cash-collect awaiting shell. */
export function readCheckoutCashCollectResult(
  sessionId: string,
): CashPaymentStartResponseDTO | null {
  try {
    const raw = window.sessionStorage.getItem(storageKey(sessionId));
    if (raw === null || raw.length === 0) {
      return null;
    }
    const parsed = JSON.parse(raw) as CashPaymentStartResponseDTO;
    if (
      typeof parsed.checkoutSessionId !== 'string' ||
      typeof parsed.amountMinor !== 'number' ||
      typeof parsed.currency !== 'string'
    ) {
      return null;
    }
    // Session-id rebind: ignore + clear foreign payload under this key.
    if (parsed.checkoutSessionId !== sessionId) {
      clearCheckoutCashCollectResult(sessionId);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function writeCheckoutCashCollectResult(
  sessionId: string,
  result: CashPaymentStartResponseDTO,
): void {
  try {
    window.sessionStorage.setItem(storageKey(sessionId), JSON.stringify(result));
  } catch {
    /* private mode */
  }
}

export function clearCheckoutCashCollectResult(sessionId: string): void {
  try {
    window.sessionStorage.removeItem(storageKey(sessionId));
  } catch {
    /* private mode */
  }
}
